import { ACCEPTED_EXT, MAX_FILE_MB } from "./attachmentConstants";

/** Har nettleseren File System Access API (mappevalg med showDirectoryPicker)? */
export const supportsDirectoryPicker = () => typeof window !== "undefined" && typeof window.showDirectoryPicker === "function" && window.isSecureContext;

export const isSupportedFile = (file) => {
  const name = (file?.name || "").toLowerCase();
  if (name.startsWith(".")) return false; // .DS_Store, skjulte filer
  return ACCEPTED_EXT.some((ext) => name.endsWith(ext));
};

/**
 * Leser alle filer rekursivt fra en mappe valgt med showDirectoryPicker.
 * Returnerer [{ file, path }] der path er relativ sti i mappen (f.eks. «Bilag 2026/mars/faktura.pdf»).
 */
export async function readDirectoryRecursive(dirHandle, prefix = "", out = [], depth = 0) {
  if (depth > 8) return out;
  for await (const [name, handle] of dirHandle.entries()) {
    const path = prefix ? `${prefix}/${name}` : name;
    if (handle.kind === "file") {
      const file = await handle.getFile();
      out.push({ file, path });
    } else if (handle.kind === "directory" && !name.startsWith(".")) {
      await readDirectoryRecursive(handle, path, out, depth + 1);
    }
  }
  return out;
}

/**
 * Åpner mappevelgeren (der den finnes). Returnerer { name, entries } eller null hvis avbrutt / ikke støttet.
 * Ved manglende støtte skal kallende kode falle tilbake til <input type="file" webkitdirectory>.
 */
export async function pickFolder() {
  if (!supportsDirectoryPicker()) return null;
  try {
    const dir = await window.showDirectoryPicker({ mode: "read" });
    const entries = await readDirectoryRecursive(dir, dir.name);
    return { name: dir.name, entries };
  } catch (e) {
    if (e?.name === "AbortError") return { cancelled: true };
    return null;
  }
}

/** Fra <input webkitdirectory>: FileList -> [{ file, path }] med relativ sti. */
export const entriesFromFileList = (fileList) => Array.from(fileList || []).map((file) => ({ file, path: file.webkitRelativePath || file.name }));

/**
 * Deler opp i støttede filer og hoppet over (feil type / for store).
 * Returnerer { ok: [{file,path}], skipped: { unsupported: n, tooBig: n } }.
 */
export function partitionEntries(entries) {
  const ok = [];
  const skipped = { unsupported: 0, tooBig: 0 };
  for (const e of entries) {
    if (!isSupportedFile(e.file)) { skipped.unsupported += 1; continue; }
    if (e.file.size > MAX_FILE_MB * 1024 * 1024) { skipped.tooBig += 1; continue; }
    ok.push(e);
  }
  ok.sort((a, b) => a.path.localeCompare(b.path, "nb"));
  return { ok, skipped };
}
